import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bot, RefreshCw, CheckCircle2, AlertTriangle, MailX, Mail, Check, BrainCircuit } from 'lucide-react';
import { toast } from 'sonner';
import { API, BulkBar, BulkCheck } from './shared';
import { MailDirective } from './MailDirective';

const STATUS = {
  'répondu': ['Répondu par IA', 'bg-emerald-500/15 text-emerald-400', CheckCircle2],
  'signalé': ['Signalé — à traiter', 'bg-amber-500/15 text-amber-400', AlertTriangle],
  'ignoré': ['Ignoré (hors support)', 'bg-gray-500/15 text-gray-400', MailX],
};

function AiNotesCard({ light, tx, th, cardCls }) {
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState('');
  const [openCard, setOpenCard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const load = () => axios.get(`${API}/admin/mailbot/notes`, { withCredentials: true })
    .then(r => setEntries(r.data.entries || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/mailbot/notes`, { text }, { withCredentials: true });
      setText('');
      setConfirmed(true);
      setTimeout(() => setConfirmed(false), 8000);
      load();
    } catch { toast.error("Erreur — la consigne n'a PAS été enregistrée"); }
    setSaving(false);
  };

  const remove = async (eid) => {
    await axios.delete(`${API}/admin/mailbot/notes/${eid}`, { withCredentials: true }).catch(() => {});
    load();
  };

  return (
    <div className={`rounded-xl border p-4 mb-5 ${cardCls}`} data-testid="ai-notes-card">
      <button onClick={() => setOpenCard(!openCard)} className="w-full flex items-center justify-between" data-testid="ai-notes-toggle">
        <span className={`text-sm font-bold ${tx}`}>
          <BrainCircuit size={15} className="inline mr-1.5 text-[#75D4D5]" />Consignes au support IA
          {entries.length > 0 && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/15 text-emerald-400">{entries.length} active{entries.length > 1 ? 's' : ''}</span>}
        </span>
        <span className={`text-[11px] ${th}`}>{openCard ? 'Fermer ▲' : 'Ouvrir ▼'}</span>
      </button>
      {openCard && (
        <div className="mt-3">
          <p className={`text-[11px] mb-2 ${th}`}>
            Écris une consigne que l'IA doit connaître (état des apps, promo, panne connue, règle de réponse…) puis clique « Envoyer ».
            <b> Prise en compte immédiate</b> et prioritaire sur ses connaissances de base.
          </p>
          {confirmed && (
            <div className="mb-2 px-3 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-xs font-bold" data-testid="ai-notes-confirmation">
              ✓ Consigne prise en compte ! Le support IA l'appliquera dès sa prochaine réponse. Elle apparaît dans la liste ci-dessous.
            </div>
          )}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="ai-notes-textarea"
            placeholder={"Ex : La nouvelle app TV Samsung est disponible depuis le 10/09, inviter les abonnés à l'installer."}
            className={`w-full px-3 py-2.5 rounded-lg border text-xs leading-relaxed ${light ? 'bg-white border-gray-300 text-gray-900' : 'bg-[#11041F] border-white/15 text-white'}`} />
          <div className="flex justify-end mt-2">
            <button onClick={send} disabled={saving || !text.trim()} data-testid="ai-notes-save"
              className="px-4 py-2 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-bold transition-colors disabled:opacity-50">
              {saving ? 'Envoi…' : 'Envoyer au support IA'}
            </button>
          </div>
          <div className="mt-3 space-y-2" data-testid="ai-notes-list">
            {entries.length === 0 && <p className={`text-[11px] ${th}`}>Aucune consigne active pour le moment.</p>}
            {[...entries].reverse().map((e) => (
              <div key={e.id} className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
                <span className="text-emerald-400 text-xs mt-0.5">●</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs leading-relaxed ${tx}`}>{e.text}</p>
                  {e.created_at && <p className={`text-[10px] mt-0.5 ${th}`}>Active depuis le {new Date(e.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>}
                </div>
                <button onClick={() => remove(e.id)} title="Supprimer cette consigne" data-testid={`ai-note-delete-${e.id}`}
                  className={`text-[11px] px-2 py-1 rounded ${light ? 'text-red-500 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/10'}`}>Supprimer</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MailBotTab({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState({ items: [], counts: {} });
  const [filter, setFilter] = useState('');
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState([]);
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const th = light ? 'text-gray-500' : 'text-white/40';
  const tx = light ? 'text-gray-900' : 'text-white';

  const load = (f = filter) => axios.get(`${API}/admin/mailbot`, { params: f ? { status: f } : {}, withCredentials: true })
    .then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const run = async () => {
    setRunning(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/mailbot/run`, {}, { withCredentials: true });
      toast.success(`${d.processed ?? 0} email(s) relevés et traités`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setRunning(false);
  };

  const resolve = async (id) => {
    await axios.post(`${API}/admin/mailbot/${id}/resolve`, {}, { withCredentials: true }).catch(() => {});
    load();
  };

  const c = data.counts || {};
  return (
    <div data-testid="mailbot-tab">
      <div className="flex items-start justify-between mb-1">
        <h1 className={`text-2xl font-bold ${tx}`}><Bot size={22} className="inline mr-2 text-[#E9641A]" />Support IA</h1>
        <button onClick={run} disabled={running} data-testid="mailbot-run-btn"
          className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-40">
          <RefreshCw size={13} className={running ? 'animate-spin' : ''} /> {running ? 'Relève en cours…' : 'Relever maintenant'}
        </button>
      </div>
      <p className={`text-xs mb-4 ${th}`}>Relève automatique de contact@mayaplus.fr toutes les 15 min : l'IA répond aux questions/problèmes d'abonnés, ignore le reste, et signale ce qu'elle ne sait pas traiter.</p>
      <AiNotesCard light={light} tx={tx} th={th} cardCls={cardCls} />
      <div className="flex flex-wrap gap-2 mb-5">
        {[['', `Tous`], ['répondu', `Répondus (${c['répondu'] || 0})`], ['signalé', `Signalés (${c['signalé'] || 0})`], ['ignoré', `Ignorés (${c['ignoré'] || 0})`]].map(([id, lbl]) => (
          <button key={id} onClick={() => setFilter(id)} data-testid={`mailbot-filter-${id || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${filter === id ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/50'}`}>{lbl}</button>
        ))}
        {(c['à traiter'] || 0) > 0 && <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-400">{c['à traiter']} à traiter</span>}
      </div>
      <BulkBar items={data.items} selected={selected} setSelected={setSelected} kind="mailbot" light={light} onDone={() => { setSelected([]); load(); }} />
      <div className="space-y-2">
        {data.items.map(m => {
          const [lbl, cls, Icon] = STATUS[m.status] || STATUS['ignoré'];
          const isOpen = open === m.id;
          return (
            <div key={m.id} className={`rounded-xl border p-3.5 ${cardCls}`} data-testid={`mailbot-item-${m.id}`}>
              <div className="flex items-start gap-3 cursor-pointer" onClick={() => setOpen(isOpen ? null : m.id)}>
                <BulkCheck id={m.id} selected={selected} setSelected={setSelected} light={light} />
                <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-none ${light ? 'bg-gray-100' : 'bg-white/5'}`}><Mail size={14} className="text-[#E9641A]" /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-xs font-semibold ${tx}`}>{m.from_name || m.from_email}</p>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cls}`}><Icon size={9} className="inline mr-1" />{lbl}</span>
                    {m.status === 'signalé' && m.resolved && <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Traité</span>}
                  </div>
                  <p className={`text-[11px] truncate ${light ? 'text-gray-600' : 'text-white/60'}`}>{m.subject || '(sans sujet)'}</p>
                  {m.summary && <p className={`text-[10px] mt-0.5 ${th}`}>{m.summary}</p>}
                  <p className={`text-[10px] mt-0.5 ${th}`}>{m.from_email} · {m.processed_at ? new Date(m.processed_at).toLocaleString('fr-FR') : ''}</p>
                </div>
                {m.status === 'signalé' && !m.resolved && (
                  <button onClick={(e) => { e.stopPropagation(); resolve(m.id); }} data-testid={`mailbot-resolve-${m.id}`}
                    className="flex items-center gap-1 text-[10px] font-bold px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 flex-none">
                    <Check size={11} /> Marquer traité
                  </button>
                )}
              </div>
              {isOpen && (
                <div className={`mt-3 pt-3 border-t text-[11px] whitespace-pre-wrap ${light ? 'border-gray-100 text-gray-700' : 'border-white/10 text-white/70'}`}>
                  <p className={`font-bold text-[10px] uppercase mb-1 ${th}`}>Message reçu</p>
                  {m.body}
                  {m.ai_reply && (<>
                    <p className={`font-bold text-[10px] uppercase mt-3 mb-1 text-emerald-400`}>Réponse envoyée par l'IA</p>
                    {m.ai_reply}
                  </>)}
                  <MailDirective mail={m} light={light} onSent={() => load()} />
                </div>
              )}
            </div>
          );
        })}
        {data.items.length === 0 && <p className={`text-center text-sm py-10 ${th}`}>Aucun email traité pour le moment. La relève tourne toutes les 15 minutes.</p>}
      </div>
    </div>
  );
}
